import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { UsersThree, Plus, Trash, Key, MagnifyingGlass } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/auth";
import { useAuth, ROLE_LABELS } from "@/lib/access";
import { AddUserDialog, ResetPasswordDialog } from "@/components/settings/UserDialogs";

const API = `${window.location.origin}/api`;
const AUTO = "__auto";
const slugOf = (email) => email.replace(/[^a-z0-9]/gi, "-");

function TeamLinkSelect({ u, teamMembers, onUpdate }) {
  if (u.role === "admin") return <span className="text-xs text-gray-400">All records</span>;
  return (
    <Select value={u.team_member || AUTO} onValueChange={(v) => onUpdate(u, { team_member: v === AUTO ? "" : v })}>
      <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`user-team-link-${slugOf(u.email)}`}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={AUTO}>{u.team_member_auto ? `Auto: ${u.team_member_auto}` : `Auto (${u.name})`}</SelectItem>
        {teamMembers.map((m) => <SelectItem key={m.id || m.name} value={m.name}>{m.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function UserRow({ u, isSelf, isNew, teamMembers, onUpdate, onDelete, onResetPassword }) {
  const slug = slugOf(u.email);
  return (
    <tr id={`user-row-${slug}`} className={`transition-colors duration-700 ${isNew ? "bg-green-50" : "hover:bg-gray-50"} ${u.disabled ? "opacity-50" : ""}`} data-testid={`user-row-${slug}`}>
      <td className="px-3 py-2.5">
        <p className="font-semibold text-gray-900 text-sm">
          {u.name} {isSelf && <span className="text-[10px] text-indigo-600 font-bold">(You)</span>}
          {isNew && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-green-600 text-white font-bold" data-testid="new-user-badge">NEW</span>}
        </p>
        <p className="text-xs text-gray-500">{u.email}</p>
      </td>
      <td className="px-3 py-2.5">
        <Select value={u.role} onValueChange={(v) => onUpdate(u, { role: v })} disabled={isSelf}>
          <SelectTrigger className="h-8 w-[120px] text-xs" data-testid={`user-role-${slug}`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
      </td>
      <td className="px-3 py-2.5"><TeamLinkSelect u={u} teamMembers={teamMembers} onUpdate={onUpdate} /></td>
      <td className="px-3 py-2.5 text-center">
        <Switch checked={u.can_edit_rates} disabled={u.role === "admin"} onCheckedChange={(v) => onUpdate(u, { can_edit_rates: v })} data-testid={`user-edit-rates-${slug}`} />
      </td>
      <td className="px-3 py-2.5 text-center">
        <Switch checked={!u.disabled} disabled={isSelf} onCheckedChange={(v) => onUpdate(u, { disabled: !v })} data-testid={`user-active-${slug}`} />
      </td>
      <td className="px-3 py-2.5">
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-indigo-600" onClick={() => onResetPassword(u)} title="Reset password" data-testid={`user-reset-pw-${slug}`}><Key size={14} /></Button>
          {!isSelf && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => onDelete(u)} title="Delete user" data-testid={`user-delete-${slug}`}><Trash size={14} /></Button>}
        </div>
      </td>
    </tr>
  );
}

export function UserManagement() {
  const me = useAuth();
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [googleOnly, setGoogleOnly] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [newEmail, setNewEmail] = useState("");

  const fetchUsers = useCallback(async () => {
    try { setUsers((await axios.get(`${API}/users`)).data); setLoadError(""); }
    catch (err) { setLoadError(getErrorMessage(err)); toast.error(getErrorMessage(err)); }
    finally { setLoaded(true); }
  }, []);

  useEffect(() => {
    fetchUsers();
    axios.get(`${API}/team`).then((r) => setTeamMembers(r.data.filter((m) => m.status === "Active"))).catch(() => {});
    axios.get(`${API}/settings/google-auth`).then((r) => setGoogleOnly(r.data.sales_google_only)).catch(() => {});
  }, [fetchUsers]);

  useEffect(() => {
    if (!newEmail) return;
    document.getElementById(`user-row-${slugOf(newEmail)}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => setNewEmail(""), 6000);
    return () => clearTimeout(t);
  }, [newEmail, users]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? users.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q)) : users;
  }, [users, search]);

  const updateUser = async (u, changes) => {
    try {
      await axios.put(`${API}/users/${encodeURIComponent(u.email)}`, changes);
      toast.success(`${u.name} updated`);
      fetchUsers();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete login for ${u.name} (${u.email})?`)) return;
    try {
      await axios.delete(`${API}/users/${encodeURIComponent(u.email)}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const onCreated = async (created) => {
    setSearch("");
    await fetchUsers();
    setNewEmail(created.email);
  };

  return (
    <Card className="p-6 border border-gray-200 bg-white" data-testid="user-management">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <UsersThree size={16} className="text-indigo-500" weight="bold" /> User Management
          <span className="text-[11px] font-bold text-gray-500 bg-gray-100 rounded px-1.5 py-0.5" data-testid="users-count">{loaded ? users.length : "..."}</span>
        </h3>
        <Button size="sm" onClick={() => setShowAdd(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 text-xs" data-testid="add-user-btn">
          <Plus size={14} className="mr-1" /> Add User
        </Button>
      </div>
      <p className="text-xs text-gray-500 mb-3">Sales Team see only their own (and unassigned) leads & customers, Inventory without prices, Product Rates and Reports. Link each login to a Team Member so their assigned leads show up.</p>
      <div className="relative mb-3">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users by name or email..." className="pl-8 h-9 text-sm" data-testid="users-search" />
      </div>
      <div className="overflow-x-auto border rounded-lg max-h-[480px] overflow-y-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-gray-50 border-b sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">User</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Role</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Team Member</th>
              <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">Edit Rates</th>
              <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">Active</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visible.map((u) => (
              <UserRow key={u.email} u={u} isSelf={u.email === me?.email} isNew={u.email === newEmail} teamMembers={teamMembers}
                onUpdate={updateUser} onDelete={deleteUser} onResetPassword={setResetUser} />
            ))}
            {!loaded && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-xs text-gray-400" data-testid="users-loading">Loading users...</td></tr>
            )}
            {loaded && loadError && (
              <tr><td colSpan={6} className="px-3 py-6 text-center" data-testid="users-load-error">
                <p className="text-xs text-red-600 font-semibold mb-2">Could not load users: {loadError}</p>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={fetchUsers} data-testid="users-retry-btn">Retry</Button>
              </td></tr>
            )}
            {loaded && !loadError && visible.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-xs text-gray-400" data-testid="users-empty">{search ? "No users match your search" : "No users yet"}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <AddUserDialog open={showAdd} onOpenChange={setShowAdd} onCreated={onCreated} teamMembers={teamMembers} googleOnly={googleOnly} />
      <ResetPasswordDialog user={resetUser} onClose={() => setResetUser(null)} onSave={(pw) => updateUser(resetUser, { password: pw }).then(() => setResetUser(null))} />
    </Card>
  );
}
